import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from '../store';
import businessTypeService from '../../API/BusinessTypesApi';
import {
	BusinessType,
	BusinessTypeState,
	BusinessTypeValues,
} from '../../components/business-type/types';

//*** Initial state
const initialState: BusinessTypeState = {
	data: [],
	isLoading: false,
};

export const asyncRetrieveBusinessTypes = createAsyncThunk<
	BusinessType[],
	void,
	{ state: RootState; rejectValue: string }
>('businessType/retrieve', async (_, thunkApi) => {
	try {
		const res = await businessTypeService.retrieveBusinessTypes();
		return res.data.data;
	} catch (error: any) {
		const errorMessage = (error.response?.data?.message ?? error.message) || error.toString();

		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncCreateBusinessType = createAsyncThunk<
	BusinessType,
	BusinessTypeValues,
	{ state: RootState; rejectValue: string }
>('businessType/create', async (payload, thunkApi) => {
	try {
		const res = await businessTypeService.createBusinessType(payload);
		toast.success(res.data.message);
		return res.data.data;
	} catch (error: any) {
		const errorMessage = (error.response?.data?.message ?? error.message) || error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncUpdateBusinessType = createAsyncThunk<
	{ id: string; updatedType: Partial<BusinessTypeValues> },
	{ id: string; updatedType: Partial<BusinessTypeValues> },
	{ state: RootState; rejectValue: string }
>('businessType/update', async ({ id, updatedType }, thunkApi) => {
	try {
		const res = await businessTypeService.updateBusinessType(id, updatedType);
		toast.success(res.data.message);
		return { id, updatedType };
	} catch (error: any) {
		const errorMessage = (error.response?.data?.message ?? error.message) || error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

export const asyncDeleteBusinessType = createAsyncThunk<
	string,
	string,
	{ state: RootState; rejectValue: string }
>('businessType/delete', async (id, thunkApi) => {
	try {
		const res = await businessTypeService.deleteBusinessType(id);
		toast.success(res.data.message);
		return id;
	} catch (error: any) {
		const errorMessage = (error.response?.data?.message ?? error.message) || error.toString();
		toast.error(errorMessage);
		return thunkApi.rejectWithValue(errorMessage);
	}
});

const BusinessTypeSlice = createSlice({
	name: 'businessType',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			//**** Retrieve Types
			.addCase(asyncRetrieveBusinessTypes.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				asyncRetrieveBusinessTypes.fulfilled,
				(state, action: PayloadAction<BusinessType[]>) => {
					state.isLoading = false;
					state.data = action.payload;
				},
			)
			.addCase(asyncRetrieveBusinessTypes.rejected, (state) => {
				state.isLoading = false;
			})

			//*****  Create business type
			.addCase(asyncCreateBusinessType.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				asyncCreateBusinessType.fulfilled,
				(state, action: PayloadAction<BusinessType>) => {
					state.isLoading = false;
					state.data = [action.payload, ...state.data];
				},
			)
			.addCase(asyncCreateBusinessType.rejected, (state) => {
				state.isLoading = false;
			})

			//****  Update type
			.addCase(
				asyncUpdateBusinessType.fulfilled,
				(
					state,
					action: PayloadAction<{
						id: string;
						updatedType: Partial<BusinessType>;
					}>,
				) => {
					const { id, updatedType } = action.payload;
					state.data = state.data.map((type) =>
						type.id === id ? { ...type, ...updatedType } : type,
					);
				},
			)
			.addCase(asyncUpdateBusinessType.rejected, (state) => {
				state.isLoading = false;
			})

			.addCase(asyncDeleteBusinessType.fulfilled, (state, action: PayloadAction<string>) => {
				state.data = state.data.filter((type) => type.id !== action.payload);
			})
			.addCase(asyncDeleteBusinessType.rejected, (state) => {
				state.isLoading = false;
			});
	},
});

export const allBusinessType = (state: RootState) => state.businessType;

export default BusinessTypeSlice.reducer;
